/**
 * SharePageLink — **分享整頁**（建立／分享／撤回一條公開連結；`decisions/031`）
 *
 * 為何要（產品負責人 2026-09-23）：「share the whole page for the 'My feeling Diary' … the date,
 *   song name, the feeling, the song with player, and the lyrics」＋ iPhone 分享檔案唔可靠
 *   → **分享一條連結**（`💬✓` 一定得），收件人喺瀏覽器睇到整頁（含播放器）。
 *
 * ⚠️ 手勢（iOS 關鍵）：`navigator.share()` 之前**唔可以** await 網絡請求 →
 *   所以係**兩步**：① 撳〔分享整頁〕建立連結（POST）② 撳〔分享連結〕即刻 share（純文字＋連結）。
 *   重建議：**唔會**自動預先建立連結（唔想為每首歌都開一條 live link）。
 *
 * ⚠️ 誠實：`audio_not_ready`／`not_found` 等錯誤一律照實顯示；撤回成功亦照實講。
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface SharePageLinkProps {
  songId: string;
  title: string;
  deviceId: string;
  /** 分享文字（父層砌；通常係「我用 CoSingery 寫咗首歌…」） */
  text: string;
  /** 有效期（日；預設 7） */
  ttlDays?: number;
  /** ⭐ 2026-09-23#6：要分享**邊一版**（例「第 2 版／共 2 版」）—— 照實講，因為只會分享揀咗嗰版 */
  versionLabel?: string;
  /** 掣面樣式（同隔籬掣一致） */
  style?: React.CSSProperties;
}

interface Created {
  url: string;
  expiresAt: string;
  reused: boolean;
}

export default function SharePageLink({ songId, title, deviceId, text, ttlDays = 7, versionLabel, style }: SharePageLinkProps) {
  const { t } = useTranslation();
  const [created, setCreated] = useState<Created | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const btn: React.CSSProperties = {
    minHeight: '36px',
    padding: '0 14px',
    borderRadius: '18px',
    border: '1.5px solid var(--color-divider)',
    background: 'var(--color-card)',
    color: 'var(--color-text)',
    fontFamily: 'inherit',
    fontSize: 'var(--font-size-small, 15px)',
    cursor: 'pointer',
    ...style,
  };

  /** ① 建立（或重用）分享連結 */
  const create = async () => {
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch(`/api/song/${encodeURIComponent(songId)}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId, ttlDays }),
      });
      const body = (await res.json().catch(() => ({}))) as { share?: Created; error?: string };
      if (!res.ok || !body.share) {
        /** 照實講（音頻未出好／唔係你嘅歌／未接通…） */
        setNote(`${t('share_page_failed')}${body.error ? `（${body.error}）` : ''}`);
        return;
      }
      setCreated(body.share);
      setNote(t('share_page_created', { days: ttlDays }));
    } catch (err) {
      setNote(`${t('share_page_failed')}（${String(err).slice(0, 60)}）`);
    } finally {
      setBusy(false);
    }
  };

  /** ② 分享連結（**即刻** share：唔可以 await 任何嘢先） */
  const shareNow = async () => {
    if (!created) return;
    const payload = `${text}\n\n${created.url}`;
    try {
      if (typeof navigator !== 'undefined' && 'share' in navigator) {
        await navigator.share({ title, text: payload });
        setNote(t('share_page_shared'));
        return;
      }
    } catch (err) {
      const name = (err as { name?: string } | null)?.name ?? '';
      /** 用戶自己取消 → 靜靜收工 */
      if (name === 'AbortError') return;
      setNote(`${t('share_page_share_failed')}（${name || 'unknown'}）`);
    }
    /** 冇 Web Share → 退 WhatsApp 連結（同 `share-button` 一致嘅做法） */
    if (typeof window !== 'undefined') {
      window.open(`https://wa.me/?text=${encodeURIComponent(payload)}`, '_blank');
    }
  };

  const copy = () => {
    if (!created) return;
    void navigator.clipboard?.writeText(created.url).catch(() => undefined);
    setNote(t('share_page_copied'));
  };

  /** 撤回（即刻失效） */
  const revoke = async () => {
    if (!created) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/share/${encodeURIComponent(created.url.split('/').pop() ?? '')}?deviceId=${encodeURIComponent(deviceId)}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setNote(`${t('share_page_failed')}${body.error ? `（${body.error}）` : ''}`);
        return;
      }
      setCreated(null);
      setNote(t('share_page_revoked_ok'));
    } catch (err) {
      setNote(`${t('share_page_failed')}（${String(err).slice(0, 60)}）`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="share-page-link" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {!created ? (
        <>
          <button type="button" data-testid="share-page-create" disabled={busy} onClick={() => void create()} style={btn}>
            {busy ? t('share_page_creating') : t('share_page_button')}
          </button>
          {/* ⚠️ 誠實：講清楚分享頁包含咩（產品負責人指示：感受文字都要顯示）＋ 邊一版 */}
          <p className="footnote" style={{ margin: 0 }} data-testid="share-page-includes">
            {t('share_page_includes')}
            {versionLabel ? ` · ${versionLabel}` : ''}
          </p>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <button type="button" data-testid="share-page-share" onClick={() => void shareNow()} style={btn}>
              {t('share_page_share')}
            </button>
            <button type="button" data-testid="share-page-copy" onClick={copy} style={btn}>
              {t('share_page_copy')}
            </button>
            <button type="button" data-testid="share-page-revoke" disabled={busy} onClick={() => void revoke()} style={btn}>
              {t('share_page_revoke')}
            </button>
          </div>
          <p className="footnote" data-testid="share-page-url" style={{ margin: 0, wordBreak: 'break-all' }}>
            {created.url}
          </p>
        </>
      )}
      {note ? (
        <p className="footnote" data-testid="share-page-note" role="status" style={{ margin: 0 }}>
          {note}
        </p>
      ) : null}
    </div>
  );
}
